import compact from 'lodash/compact';
import upperFirst from 'lodash/upperFirst';
import type { Moment } from 'moment';

import type { AlertCommonProps } from '@front/common/src/components/alert/types';
import type { BasketState } from '@front/common/src/ducks/common/basket';
import type { Promotion } from '@front/common/src/types/commons';
import { formatPrimaryAppointment } from '@front/common/src/utils/appointment';
import { hasPrestations } from '@front/common/src/utils/basket';
import { isRecurrentRecurrence } from '@front/common/src/utils/cleaning';
import { FORMATS, formatDate } from '@front/common/src/utils/date';
import { getMessages } from '@front/common/src/utils/errors';

import { translate } from '@front/i18n';

const t = translate('prices');

type ShouldFormatPromotionPerHourParams = {
  promotion?: Promotion;
  duration?: BasketState['duration'];
};

export const shouldFormatPromotionPerHour = ({ promotion, duration }: ShouldFormatPromotionPerHourParams) =>
  promotion?.calculation_type === 'per_hour' && !duration;

type GetPromotionAlertContent = (params: { promotion?: Promotion; error?: Error | null }) => {
  type: Extract<AlertCommonProps['type'], 'success' | 'error'>;
  content: string | string[];
};

export const getPromotionAlertContent: GetPromotionAlertContent = ({ promotion, error }) => {
  if (promotion?.success) {
    return {
      type: 'success',
      content: t('components.promo_code.success', { code: promotion.code }),
    };
  }

  return {
    type: 'error',
    // Backend validation errors are in promotion.message, error is for unexpected errors
    content: compact([...(error ? getMessages(error) : []), promotion?.message]),
  };
};

type GetAppointmentDescriptionIfNoSecondaryProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
  isOnCalendarScreen?: boolean;
};

export const getAppointmentDescriptionIfNoSecondary = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
  isOnCalendarScreen = false,
}: GetAppointmentDescriptionIfNoSecondaryProps) => {
  const isRecurrent = isRecurrentRecurrence(weeklyRecurrence);
  const type = isRecurrent ? 'recurrent' : 'spot';
  if (primary) {
    return t(`basket.cleaning.appointment_hour.primary_with_no_secondary.${type}.description`);
  }
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.description`);
  }
  if (isOnCalendarScreen) {
    return t('basket.cleaning.appointment_calendar.date_selected_only.description');
  }
  if (!isRecurrent) {
    return t('basket.cleaning.appointment_hour.date_selected_only.spot.description');
  }
  if (weeklyRecurrence && weeklyRecurrence > 1) {
    return t('basket.cleaning.appointment_hour.date_selected_only.several.description');
  }
  return t('basket.cleaning.appointment_hour.date_selected_only.weekly.description');
};

type GetAppointmentAccordionTitleProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
};

export const getAppointmentAccordionTitle = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
}: GetAppointmentAccordionTitleProps) => {
  const type = isRecurrentRecurrence(weeklyRecurrence) ? 'recurrent' : 'spot';

  // After booking creation
  if (primary) {
    return formatPrimaryAppointment(primary, weeklyRecurrence);
  }

  // Before booking creation (inside funnel), no primary yet selected
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.title`);
  }

  // Before booking creation (inside funnel), primary selected for recurrent cleaning
  if (weeklyRecurrence) {
    const date = formatDate(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH_SHORT);
    if (weeklyRecurrence === 0.25) {
      return t('appointment.primary.every_4_weeks', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 0.5) {
      return t('appointment.primary.biweekly', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 1) {
      return t('appointment.primary.weekly', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence > 1) {
      return t('appointment.primary.several', { first: date, recurrence: weeklyRecurrence });
    }
  }

  return upperFirst(formatDate(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH));
};

export type HasBasketReachedMinimumPriceArgs = {
  basketContent: BasketState['basketContent'];
  currentPrice: number;
  minimumPrice: number;
  minPriceSurchargeAccepted: boolean;
};

export const hasBasketReachedMinimumPrice = ({
  basketContent,
  currentPrice,
  minimumPrice,
  minPriceSurchargeAccepted,
}: HasBasketReachedMinimumPriceArgs) => {
  if (!hasPrestations(basketContent)) return false;
  if (minPriceSurchargeAccepted) return true;

  return currentPrice >= minimumPrice;
};
